import React, { useContext, useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import { TextInput, Label, Select } from "flowbite-react";
import DataTable from "react-data-table-component";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const parseToLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }
  const dmyMatch = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmyMatch) {
    return new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
  }
  const d = new Date(s);
  if (!isNaN(d)) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
};

const Report = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    purchase: [],
    sales: [],
    service_log: [],
    income: [],
    expenses: [],
    pending_customers: [],
    invoice_summary: [],
    service_summary: [],
    ledger: { entries: [], total_credit: 0, total_debit: 0, final_balance: 0 },
  });
  const [activeTab, setActiveTab] = useState("sales");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [serviceSummaryData, setServiceSummaryData] = useState([]);

  // ── NEW: invoice summary stored in state just like service summary ──
  const [invoiceSummaryData, setInvoiceSummaryData] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [profitLossData, setProfitLossData] = useState([]);
  const [monthlyGraphData, setMonthlyGraphData] = useState({ labels: [], income: [], expense: [] });

  useEffect(() => {
    fetchData();
    fetchIncomeCategories();
    fetchExpenseCategories();
  }, []);

  const fetchData = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");
    fetch(`${globalValue}/report?branch=${encodeURIComponent(branch)}`, {
      method: "GET",
      headers: { "x-access-token": sessionStorage.getItem("token") },
    })
      .then((res) => res.json())
      .then((dat) => {
        const initialData = {
          purchase: [], sales: [], service_log: [],
          income: [], expenses: [], pending_customers: [],
          invoice_summary: [], service_summary: [],
          ledger: { entries: [], total_credit: 0, total_debit: 0, final_balance: 0 },
        };
        setData({ ...initialData, ...dat });
      })
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  };

  const fetchIncomeCategories = () => {
    setLoading(true);
    axios.get(`${globalValue}/category_income_display`)
      .then((res) => setIncomeCategories(res.data))
      .catch((err) => console.error("Error fetching income categories:", err))
      .finally(() => setLoading(false));
  };

  const fetchExpenseCategories = () => {
    setLoading(true);
    axios.get(`${globalValue}/category_display`)
      .then((res) => setExpenseCategories(res.data))
      .catch((err) => console.error("Error fetching expense categories:", err))
      .finally(() => setLoading(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = parseToLocalDate(dateStr);
    if (!d) return "N/A";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const filterByDate = (items, dateKey) => {
    if (!Array.isArray(items)) return [];
    let from = null;
    let to = null;
    if (fromDate) {
      const [fy, fm, fd] = fromDate.split("-").map(Number);
      from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
    }
    if (toDate) {
      const [ty, tm, td] = toDate.split("-").map(Number);
      to = new Date(ty, tm - 1, td, 23, 59, 59, 999);
    }
    if (!from && !to) return items;
    return items.filter((item) => {
      const itemDate = parseToLocalDate(item[dateKey]);
      if (!itemDate) return false;
      return (!from || itemDate >= from) && (!to || itemDate <= to);
    });
  };

  const getFilteredLedger = () => {
    const allEntries = data.ledger?.entries || [];
    let from = null;
    let to = null;
    if (fromDate) {
      const [fy, fm, fd] = fromDate.split("-").map(Number);
      from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
    }
    if (toDate) {
      const [ty, tm, td] = toDate.split("-").map(Number);
      to = new Date(ty, tm - 1, td, 23, 59, 59, 999);
    }

    let openingBalance = 0;
    if (from) {
      allEntries.forEach((entry) => {
        const d = parseToLocalDate(entry.date);
        if (d && d < from) {
          openingBalance += (entry.credit || 0) - (entry.debit || 0);
        }
      });
    }

    let filtered = allEntries.filter((entry) => {
      const d = parseToLocalDate(entry.date);
      if (!d) return false;
      return (!from || d >= from) && (!to || d <= to);
    });

    if (search.trim()) {
      const keyword = search.toLowerCase();
      filtered = filtered.filter((row) =>
        String(row.particulars || "").toLowerCase().includes(keyword) ||
        String(row.credit || "").includes(keyword) ||
        String(row.debit || "").includes(keyword)
      );
    }

    let running = openingBalance;
    const recalculated = filtered.map((entry, idx) => {
      const opening = running;
      running = running + (entry.credit || 0) - (entry.debit || 0);
      return {
        ...entry,
        _row_key: `ledger_${idx}`,
        sno: idx + 1,
        opening: Math.round(opening * 100) / 100,
        closing: Math.round(running * 100) / 100,
      };
    });

    const total_credit = filtered.reduce((s, e) => s + (e.credit || 0), 0);
    const total_debit  = filtered.reduce((s, e) => s + (e.debit  || 0), 0);
    
    // Split total credit into cash and online
    const total_credit_cash = filtered.reduce((sum, entry) => {
      if (entry.credit > 0 && String(entry.payment_type || "").toLowerCase() === "cash") {
        return sum + (entry.credit || 0);
      }
      return sum;
    }, 0);
    const total_credit_online = total_credit - total_credit_cash;

    return {
      entries: recalculated,
      opening_balance: Math.round(openingBalance * 100) / 100,
      total_credit: Math.round(total_credit * 100) / 100,
      total_credit_cash: Math.round(total_credit_cash * 100) / 100,
      total_credit_online: Math.round(total_credit_online * 100) / 100,
      total_debit:  Math.round(total_debit  * 100) / 100,
      final_balance: Math.round(running * 100) / 100,
    };
  };

  const getFilteredData = (currentTab) => {
    let itemsToFilter = [];
    let dateKey = "date";

    if (currentTab === "sales")                  itemsToFilter = data.sales;
    else if (currentTab === "purchase")          itemsToFilter = data.purchase;
    else if (currentTab === "income")            itemsToFilter = data.income;
    else if (currentTab === "expenses")          itemsToFilter = data.expenses;
    else if (currentTab === "service_log")     { itemsToFilter = data.service_log; dateKey = "s_date"; }
    else if (currentTab === "pending_customers") itemsToFilter = data.pending_customers;
    // ── invoice_summary now uses the processed state, not raw data ──
    else if (currentTab === "invoice_summary")   itemsToFilter = invoiceSummaryData;

    let filteredItems =
      currentTab === "pending_customers"
        ? Array.isArray(itemsToFilter) ? [...itemsToFilter] : []
        // invoice_summary is already date-filtered in useEffect, skip filterByDate
        : currentTab === "invoice_summary"
          ? Array.isArray(itemsToFilter) ? [...itemsToFilter] : []
          : filterByDate(itemsToFilter, dateKey);

    if (currentTab === "income" || currentTab === "expenses") {
      if (selectedCategory === "others") {
        filteredItems = filteredItems.filter(
          (item) => !item.category || String(item.category || "").trim() === ""
        );
      } else if (selectedCategory) {
        filteredItems = filteredItems.filter(
          (item) => String(item.category || "").toLowerCase().includes(selectedCategory.toLowerCase())
        );
      }
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      if (currentTab === "income") {
        filteredItems = filteredItems.filter((row) =>
          String(row.category || "").toLowerCase().includes(keyword) ||
          String(row.amount || "").includes(keyword) ||
          String(row.mode || "").toLowerCase().includes(keyword) ||
          String(row.customer_name || "").toLowerCase().includes(keyword) ||
          String(row.vehicle_no || "").toLowerCase().includes(keyword) ||
          String(row.bill_no || "").toLowerCase().includes(keyword)
        );
      } else if (currentTab === "expenses") {
        filteredItems = filteredItems.filter((row) =>
          String(row.category || "").toLowerCase().includes(keyword) ||
          String(row.amount || "").includes(keyword) ||
          String(row.mode || "").toLowerCase().includes(keyword)
        );
      } else if (currentTab === "purchase") {
        filteredItems = filteredItems.filter((row) =>
          String(row.bill_no || "").toLowerCase().includes(keyword) ||
          String(row.vendor_name || "").toLowerCase().includes(keyword) ||
          String(row.total_amount || "").includes(keyword)
        );
      } else if (currentTab === "sales") {
        filteredItems = filteredItems.filter((row) =>
          String(row.name || "").toLowerCase().includes(keyword) ||
          String(row.mobile_no || "").toLowerCase().includes(keyword) ||
          String(row.grand_total || "").includes(keyword)
        );
      } else if (currentTab === "service_log") {
        filteredItems = filteredItems.filter((row) =>
          String(row.customer_name || "").toLowerCase().includes(keyword) ||
          String(row.mobile_no || "").toLowerCase().includes(keyword) ||
          String(row.estimate_id || "").toLowerCase().includes(keyword)
        );
      } else if (currentTab === "pending_customers") {
        filteredItems = filteredItems.filter((row) =>
          String(row.customer_name || "").toLowerCase().includes(keyword) ||
          String(row.mobile_no || "").toLowerCase().includes(keyword) ||
          String(row.vehicle_no || "").toLowerCase().includes(keyword) ||
          String(row.brand || "").toLowerCase().includes(keyword) ||
          String(row.model || "").toLowerCase().includes(keyword) ||
          String(row.address || "").toLowerCase().includes(keyword)
        );
      } else if (currentTab === "invoice_summary") {
        filteredItems = filteredItems.filter((row) =>
          String(row.customer_name || "").toLowerCase().includes(keyword) ||
          String(row.mobile_no || "").toLowerCase().includes(keyword) ||
          String(row.vehicle_no || "").toLowerCase().includes(keyword) ||
          String(row.invoice_no || "").toLowerCase().includes(keyword) ||
          String(formatDate(row.date) || "").toLowerCase().includes(keyword)
        );
      }
    }

    return filteredItems.map((item, idx) => ({ ...item, _row_key: `${currentTab}_${idx}` }));
  };

  const formatNumber = (num) =>
    Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getTotalAmount = (currentTab, filteredData) => {
    if (!Array.isArray(filteredData)) return 0;
    switch (currentTab) {
      case "sales":           return filteredData.reduce((s, i) => s + (Number(i.grand_total) || 0), 0);
      case "purchase":        return filteredData.reduce((s, i) => s + (Number(i.total_amount) || 0), 0);
      case "income":
      case "expenses":        return filteredData.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      case "invoice_summary": return filteredData.reduce((s, i) => s + (Number(i.total_labour_charge) || 0), 0);
      default:                return 0;
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const title = "JALS AUTOMOBILES - " + activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + " Report";
    doc.text(title, 14, 10);

    let startY = 15;
    if (fromDate || toDate) {
      doc.setFontSize(10);
      const fromText = fromDate ? `From: ${formatDate(fromDate)}` : "";
      const toText   = toDate   ? `To: ${formatDate(toDate)}`     : "";
      doc.text(`${fromText}   ${toText}`, 14, 16);
      startY = 25;
    }

    let rows = [];
    let columns = [];
    const tableData = getFilteredData(activeTab);

    if (activeTab === "sales") {
      columns = ["Date", "Name", "Mobile", "Address", "Total"];
      rows = tableData.map((row) => [formatDate(row.date), row.name, row.mobile_no, row.address, row.grand_total]);
    } else if (activeTab === "purchase") {
      columns = ["Date", "Bill No", "Vendor", "Total", "Paid", "Mode"];
      rows = tableData.map((row) => [formatDate(row.date), row.bill_no, row.vendor_name, row.total_amount, row.amount_paid, row.pay_mode]);
    } else if (activeTab === "income") {
      columns = ["Date", "Invoice No", "Customer Name", "Vehicle No", "Category", "Amount", "Mode"];
      rows = tableData.map((row) => [formatDate(row.date), row.bill_no || "-", row.customer_name || "-", row.vehicle_no || "-", row.category, Number(row.amount || 0).toFixed(2), row.mode]);
    } else if (activeTab === "expenses") {
      columns = ["Date", "Category", "Amount", "Staff/Expense"];
      rows = tableData.map((row) => [formatDate(row.date), row.category, Number(row.amount || 0).toFixed(2), row.mode]);
    } else if (activeTab === "service_log") {
      columns = ["Start Date", "Estimate ID", "Customer", "End Date", "Mode"];
      rows = tableData.map((row) => [formatDate(row.s_date), row.estimate_id, row.customer_name, formatDate(row.e_date), row.mode]);
    } else if (activeTab === "pending_customers") {
      columns = ["Customer Name", "Mobile No", "Brand", "Model", "Vehicle No", "Address"];
      rows = tableData.map((row) => [row.customer_name, row.mobile_no, row.brand, row.model, row.vehicle_no, row.address]);
    } else if (activeTab === "invoice_summary") {
      columns = ["Date", "Invoice No", "Customer Name", "Mobile No", "Vehicle No", "Labour Charge"];
      rows = tableData.map((row) => [formatDate(row.date), row.invoice_no, row.customer_name, row.mobile_no, row.vehicle_no, formatNumber(row.total_labour_charge)]);
    } else if (activeTab === "service_summary") {
      columns = ["Service Name", "Date", "Amount"];
      serviceSummaryData.forEach(group => {
        group.entries.forEach((entry, idx) => {
          rows.push([idx === 0 ? group.service_name.toUpperCase() : "", formatDate(entry.date), formatNumber(entry.amount)]);
        });
        rows.push(["", "SUBTOTAL", formatNumber(group.total)]);
      });
      rows.push(["", "GRAND TOTAL", formatNumber(serviceSummaryData.reduce((s, g) => s + g.total, 0))]);
    } else if (activeTab === "profit_loss") {
      columns = ["Month", "Total Income", "Total Expense", "Profit / Loss"];
      rows = profitLossData.map((row) => [row.month, formatNumber(row.income), formatNumber(row.expense), formatNumber(row.profit)]);
    } else if (activeTab === "opening_closing") {
      const ledger = getFilteredLedger();
      columns = ["S.No", "Date", "Particulars", "Opening (₹)", "Credit (Inc)", "Debit (Exp)", "Closing (₹)"];
      rows = ledger.entries.map((row) => [
        row.sno,
        formatDate(row.date),
        row.particulars,
        formatNumber(row.opening),
        formatNumber(row.credit),
        formatNumber(row.debit),
        formatNumber(row.closing),
      ]);
      rows.unshift(["", "", "OPENING BALANCE", "", "", "", formatNumber(ledger.opening_balance)]);
      rows.push(["", "", "TOTAL", "", formatNumber(ledger.total_credit), formatNumber(ledger.total_debit), ""]);
      rows.push(["", "", "CLOSING BALANCE", "", "", "", formatNumber(ledger.final_balance)]);
    }

    if (
      rows.length > 0 &&
      activeTab !== "service_log" &&
      activeTab !== "pending_customers" &&
      activeTab !== "opening_closing" &&
      activeTab !== "service_summary"
    ) {
      let sumColumnIndex = null;
      const findIndex = activeTab === "purchase" ? 3 : 1;
      for (let i = rows[0].length - findIndex; i >= 0; i--) {
        if (rows.some((r) => !isNaN(parseFloat(r[i])))) { sumColumnIndex = i; break; }
      }
      if (sumColumnIndex !== null) {
        const totalValue = rows.reduce((sum, r) => {
          const valStr = String(r[sumColumnIndex]).replace(/,/g, "");
          const v = parseFloat(valStr);
          return sum + (isNaN(v) ? 0 : v);
        }, 0);
        const totalRow = Array(rows[0].length).fill("");
        totalRow[sumColumnIndex - 1] = "TOTAL";
        totalRow[sumColumnIndex] = formatNumber(totalValue);
        rows.push(totalRow);
      }
    }

    autoTable(doc, {
      head: [columns], body: rows, startY, styles: { fontSize: 8 },
      didParseCell: (data) => {
        if (
          data.row.index === rows.length - 1 &&
          activeTab !== "service_log" &&
          activeTab !== "pending_customers" &&
          activeTab !== "service_summary"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 220, 220];
        }
        if (activeTab === "service_summary") {
          if (data.cell.text[0] === "SUBTOTAL" || data.cell.text[0] === "GRAND TOTAL") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 240, 240];
          }
        }
      },
    });
    doc.save(`${activeTab}_report.pdf`);
  };

  const customStyles = {
    table:      { style: { backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
    headCells:  { style: { backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)", color: theme === "dark" ? "white" : "black", fontWeight: "bold" } },
    cells:      { style: { color: theme === "dark" ? "white" : "black", backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
    pagination: { style: { color: theme === "dark" ? "white" : "black", backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white" } },
  };

  const KEY_FIELD = "_row_key";

  const salesColumns = [
    { name: "Date",    selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Name",    selector: (r) => r.name,             sortable: true },
    { name: "Mobile",  selector: (r) => r.mobile_no },
    { name: "Address", selector: (r) => r.address },
    { name: "Total",   sortable: true, selector: (r) => Number(r.grand_total), cell: (r) => Number(r.grand_total).toFixed(2) },
  ];

  const purchaseColumns = [
    { name: "Date",    selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Bill No", selector: (r) => r.bill_no,          sortable: true },
    { name: "Vendor",  selector: (r) => r.vendor_name,      sortable: true },
    { name: "Total",   sortable: true, selector: (r) => Number(r.total_amount || 0), cell: (r) => Number(r.total_amount || 0).toFixed(2) },
    { name: "Mode",    selector: (r) => r.pay_mode },
  ];

  const incomeColumns = [
    { name: "Date",          selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Invoice No",    selector: (r) => r.bill_no || "-",   sortable: true },
    { name: "Customer Name", selector: (r) => r.customer_name || "-", sortable: true },
    { name: "Vehicle No",    selector: (r) => r.vehicle_no || "-",    sortable: true },
    { name: "Category",      selector: (r) => r.category,         sortable: true },
    { name: "Amount",        sortable: true, selector: (r) => Number(r.amount || 0), cell: (r) => Number(r.amount || 0).toFixed(2) },
    { name: "Mode",          selector: (r) => r.mode,             sortable: true },
  ];

  const expenseColumns = [
    { name: "Date",          selector: (r) => formatDate(r.date), sortable: true, width: "120px" },
    { name: "Category",      selector: (r) => r.category,         sortable: true },
    { name: "Amount",        sortable: true, selector: (r) => Number(r.amount || 0), cell: (r) => Number(r.amount || 0).toFixed(2) },
    { name: "Staff/Expense", selector: (r) => r.mode,             sortable: true },
  ];

  const serviceColumns = [
    { name: "Start Date",  selector: (r) => formatDate(r.s_date), sortable: true, width: "120px" },
    { name: "Estimate ID", selector: (r) => r.estimate_id },
    { name: "Customer",    selector: (r) => r.customer_name, sortable: true },
    { name: "Mobile No",   selector: (r) => r.mobile_no },
    { name: "End Date",    selector: (r) => formatDate(r.e_date), sortable: true },
    { name: "Mode",        selector: (r) => r.mode },
  ];

  const pendingCustomerColumns = [
    { name: "Customer Name", selector: (r) => r.customer_name, sortable: true },
    { name: "Mobile No",     selector: (r) => r.mobile_no },
    { name: "Brand",         selector: (r) => r.brand },
    { name: "Model",         selector: (r) => r.model },
    { name: "Vehicle No",    selector: (r) => r.vehicle_no },
    { name: "Address",       selector: (r) => r.address },
  ];

  const invoiceSummaryColumns = [
    { name: "Date",          selector: (r) => formatDate(r.date),  sortable: true, width: "120px" },
    { name: "Invoice No",    selector: (r) => r.invoice_no,        sortable: true },
    { name: "Customer Name", selector: (r) => r.customer_name,     sortable: true },
    { name: "Mobile No",     selector: (r) => r.mobile_no },
    { name: "Vehicle No",    selector: (r) => r.vehicle_no || "-", sortable: true },
    { name: "Labour Charge", selector: (r) => Number(r.total_labour_charge || 0), cell: (r) => formatNumber(r.total_labour_charge || 0), sortable: true },
  ];

  const profitLossColumns = [
    { name: "Month",         selector: (r) => r.month,           sortable: true },
    { name: "Total Income",  sortable: true, selector: (r) => Number(r.income),  cell: (r) => formatNumber(r.income) },
    { name: "Total Expense", sortable: true, selector: (r) => Number(r.expense), cell: (r) => formatNumber(r.expense) },
    {
      name: "Profit / Loss", sortable: true, selector: (r) => Number(r.profit),
      cell: (r) => (
        <span className={r.profit >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
          {formatNumber(r.profit)}
        </span>
      ),
    },
  ];

  const ledgerColumns = [
    { name: "S.No",         selector: (r) => r.sno,                   sortable: true, width: "70px" },
    { name: "Date",         selector: (r) => formatDate(r.date),       sortable: true, width: "110px" },
    { name: "Particulars",  selector: (r) => r.particulars,            sortable: true, wrap: true },
    {
      name: "Opening (₹)",
      selector: (r) => Number(r.opening),
      sortable: true,
      cell: (r) => <span className="text-gray-600">{formatNumber(r.opening)}</span>,
    },
    {
      name: "Credit (Inc)",
      selector: (r) => Number(r.credit),
      sortable: true,
      cell: (r) => (
        <span className={r.credit > 0 ? "text-green-600 font-semibold" : "text-gray-400"}>
          {r.credit > 0 ? formatNumber(r.credit) : "-"}
        </span>
      ),
    },
    {
      name: "Debit (Exp)",
      selector: (r) => Number(r.debit),
      sortable: true,
      cell: (r) => (
        <span className={r.debit > 0 ? "text-red-600 font-semibold" : "text-gray-400"}>
          {r.debit > 0 ? formatNumber(r.debit) : "-"}
        </span>
      ),
    },
    {
      name: "Closing (₹)",
      selector: (r) => Number(r.closing),
      sortable: true,
      cell: (r) => (
        <span className={r.closing >= 0 ? "text-blue-700 font-bold" : "text-red-700 font-bold"}>
          {formatNumber(r.closing)}
        </span>
      ),
    },
  ];

  const tabs = [
    "sales", "purchase", "income", "expenses",
    "service_log", "pending_customers", "invoice_summary", "service_summary", "profit_loss", "opening_closing"
  ];

  const getMonthlySummary = (items, dateKey) => {
    if (!Array.isArray(items)) return {};
    const monthly = {};
    items.forEach((item) => {
      const d = parseToLocalDate(item[dateKey]);
      if (d && !isNaN(d)) {
        const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        if (!monthly[key]) monthly[key] = 0;
        monthly[key] += Number(item.amount ?? item.total_amount ?? item.grand_total ?? 0);
      }
    });
    return monthly;
  };

  useEffect(() => {
    const incomeFiltered  = filterByDate(data.income,   "date");
    const expenseFiltered = filterByDate(data.expenses, "date");

    const incomeMonthly  = getMonthlySummary(incomeFiltered,  "date");
    const expenseMonthly = getMonthlySummary(expenseFiltered, "date");

    const labels = Array.from(
      new Set([...Object.keys(incomeMonthly), ...Object.keys(expenseMonthly)])
    ).sort((a, b) => {
      const [am, ay] = a.split("/"); const [bm, by] = b.split("/");
      return new Date(`${ay}-${am}-01`) - new Date(`${by}-${bm}-01`);
    });

    let plTbl = labels.map((label) => {
      const inc = incomeMonthly[label]  || 0;
      const exp = expenseMonthly[label] || 0;
      return { month: label, income: inc, expense: exp, profit: inc - exp };
    });

    if (search.trim()) {
      const keyword = search.toLowerCase();
      plTbl = plTbl.filter((row) =>
        String(row.month || "").toLowerCase().includes(keyword) ||
        String(row.income || "").includes(keyword) ||
        String(row.expense || "").includes(keyword) ||
        String(row.profit || "").includes(keyword)
      );
    }

    if (plTbl.length) {
      plTbl.push({
        month: "Total",
        income:  plTbl.reduce((a, b) => a + b.income,  0),
        expense: plTbl.reduce((a, b) => a + b.expense, 0),
        profit:  plTbl.reduce((a, b) => a + b.income - b.expense, 0),
      });
    }

    setProfitLossData(plTbl.map((row, idx) => ({ ...row, _row_key: `pl_${idx}` })));
    setMonthlyGraphData({
      labels,
      income:  labels.map((l) => incomeMonthly[l]  || 0),
      expense: labels.map((l) => expenseMonthly[l] || 0),
    });

    // ── Service Summary: filter entries by date, skip zero amounts ──
    let ssFiltered = (data.service_summary || []).map(group => {
      let filteredEntries = (group.entries || []).filter(entry => Number(entry.amount || 0) > 0);
      if (fromDate || toDate) {
        filteredEntries = filterByDate(filteredEntries, "date");
      }
      return {
        ...group,
        entries: filteredEntries,
        total: filteredEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0)
      };
    }).filter(group => group.entries.length > 0);

    if (search.trim()) {
      const keyword = search.toLowerCase();
      ssFiltered = ssFiltered.filter(group =>
        String(group.service_name || "").toLowerCase().includes(keyword) ||
        group.entries.some(entry =>
          String(entry.amount || "").includes(keyword) ||
          String(entry.date || "").includes(keyword)
        )
      );
    }
    setServiceSummaryData(ssFiltered);

    // invoice_summary: backend sends one flat row per service_log entry.
    // date = s_date, total_labour_charge = sum of all labour items in that row.
    // Just filter these flat rows by date — same s_date used by service_summary entries.
    // So both totals will always match for any date range.
    let invFiltered = filterByDate(data.invoice_summary || [], "date");

    setInvoiceSummaryData(invFiltered);

  }, [fromDate, toDate, data, search]);

  const lineData = {
    labels: monthlyGraphData.labels,
    datasets: [
      { label: "Income",   data: monthlyGraphData.income,  borderColor: "#7c3aed", backgroundColor: "rgba(124,58,237,0.08)", tension: 0.3, fill: false },
      { label: "Expenses", data: monthlyGraphData.expense, borderColor: "#f87171", backgroundColor: "rgba(248,113,113,0.08)", tension: 0.3, fill: false },
    ],
  };

  const lineOptions = {
    plugins: { legend: { labels: { color: theme === "dark" ? "white" : "#222" } } },
    scales: {
      x: { ticks: { color: theme === "dark" ? "white" : "#222" } },
      y: { ticks: { color: theme === "dark" ? "white" : "#222" } },
    },
  };

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}
      <div className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
        <div className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${theme === "dark" ? "bg-gray-800 text-gray-200" : "bg-white text-gray-800"}`}>

          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Report Summary</h2>
            <button onClick={handleExportPDF} className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded">
              Export PDF
            </button>
          </div>

          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex flex-col">
              <Label htmlFor="fromDate" value="From Date" className="mb-1 text-sm" />
              <input id="fromDate" type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"}`}
              />
            </div>
            <div className="flex flex-col">
              <Label htmlFor="toDate" value="To Date" className="mb-1 text-sm" />
              <input id="toDate" type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"}`}
              />
            </div>

            {activeTab === "income" && (
              <div className="flex gap-4 flex-wrap flex-1 min-w-[200px]">
                <div className="flex flex-col">
                  <Label htmlFor="categoryFilter" value="Filter by Category" className="mb-1 text-sm" />
                  <Select id="categoryFilter" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                    className={theme === "dark" ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}>
                    <option value="">All Categories</option>
                    {incomeCategories.map((cat) => <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>)}
                    <option value="others">Others</option>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                  <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice, customer, vehicle, amount..." />
                </div>
              </div>
            )}

            {activeTab === "expenses" && (
              <div className="flex gap-4 flex-wrap flex-1 min-w-[200px]">
                <div className="flex flex-col">
                  <Label htmlFor="categoryFilter" value="Filter by Category" className="mb-1 text-sm" />
                  <Select id="categoryFilter" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                    className={theme === "dark" ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}>
                    <option value="">All Categories</option>
                    {expenseCategories.map((cat) => <option key={cat.id} value={cat.category_name}>{cat.category_name}</option>)}
                    <option value="others">Others</option>
                  </Select>
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                  <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Category, amount, mode..." />
                </div>
              </div>
            )}

            {activeTab !== "income" && activeTab !== "expenses" && (
              <div className="flex flex-col flex-1 min-w-[200px]">
                <Label htmlFor="search" value="Search" className="mb-1 text-sm" />
                <TextInput id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." />
              </div>
            )}
          </div>

          <Tab.Group selectedIndex={tabs.indexOf(activeTab)} onChange={(i) => { setActiveTab(tabs[i]); }}>
            <Tab.List className="flex justify-between w-full mb-4 border-b bg-gray-100 rounded-t-lg overflow-hidden">
              {tabs.map((tab) => (
                <Tab key={tab} className={({ selected }) =>
                  `flex-1 text-center px-6 py-3 transition-all duration-200 text-sm md:text-base ${selected ? "bg-blue-600 text-white font-semibold" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`
                }>
                  {tab.replace(/_/g, " ").split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                </Tab>
              ))}
            </Tab.List>

            <Tab.Panels>
              <Tab.Panel>
                <DataTable columns={salesColumns} data={getFilteredData("sales")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("sales", getFilteredData("sales")))}
                </div>
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={purchaseColumns} data={getFilteredData("purchase")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("purchase", getFilteredData("purchase")))}
                </div>
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={incomeColumns} data={getFilteredData("income")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("income", getFilteredData("income")))}
                </div>
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={expenseColumns} data={getFilteredData("expenses")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("expenses", getFilteredData("expenses")))}
                </div>
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={serviceColumns} data={getFilteredData("service_log")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={pendingCustomerColumns} data={getFilteredData("pending_customers")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
              </Tab.Panel>

              <Tab.Panel>
                <DataTable columns={invoiceSummaryColumns} data={getFilteredData("invoice_summary")}
                  keyField={KEY_FIELD} customStyles={customStyles} pagination striped highlightOnHover />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹ {formatNumber(getTotalAmount("invoice_summary", getFilteredData("invoice_summary")))}
                </div>
              </Tab.Panel>

              <Tab.Panel>
                {serviceSummaryData.length > 0 ? (
                  <div className="space-y-6">
                    {serviceSummaryData.map((group, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${theme === "dark" ? "border-gray-700 bg-gray-750" : "border-gray-200 bg-gray-50"}`}>
                        <div className="flex justify-between items-center mb-4 pb-2 border-b">
                          <h3 className="text-lg font-bold text-blue-600 uppercase">{group.service_name}</h3>
                          <span className="text-lg font-bold">Total: ₹ {formatNumber(group.total)}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left">
                            <thead className={`text-xs uppercase ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                              <tr>
                                <th className="px-4 py-2">S.No</th>
                                <th className="px-4 py-2">Date</th>
                                <th className="px-4 py-2 text-right">Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.entries.map((entry, eIdx) => (
                                <tr key={eIdx} className={`border-t ${theme === "dark" ? "border-gray-700" : "border-gray-200"}`}>
                                  <td className="px-4 py-2">{eIdx + 1}</td>
                                  <td className="px-4 py-2">{formatDate(entry.date)}</td>
                                  <td className="px-4 py-2 text-right font-semibold">{formatNumber(entry.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    <div className="p-4 text-right bg-blue-600 text-white rounded-lg font-bold text-xl">
                      Grand Total: ₹ {formatNumber(serviceSummaryData.reduce((sum, g) => sum + g.total, 0))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-10 text-gray-500">No service summary data found.</p>
                )}
              </Tab.Panel>

              <Tab.Panel>
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row flex-wrap gap-8 justify-center items-center mb-8">
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Income</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].income : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Expense</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].expense : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-green-300">
                      <h3 className="text-2xl font-semibold text-green-700 mb-3">Profit</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(profitLossData.length && profitLossData[profitLossData.length - 1].profit > 0 ? profitLossData[profitLossData.length - 1].profit : 0)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-red-300">
                      <h3 className="text-2xl font-semibold text-red-700 mb-3">Loss</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(profitLossData.length && profitLossData[profitLossData.length - 1].profit < 0 ? Math.abs(profitLossData[profitLossData.length - 1].profit) : 0)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 w-full max-w-3xl mx-auto">
                    <h3 className="text-lg font-semibold mb-3 text-purple-800 text-center">Monthly Income & Expense Trend</h3>
                    <Line data={lineData} options={lineOptions} height={110} />
                  </div>
                  <div className="mt-8">
                    <DataTable columns={profitLossColumns} data={profitLossData}
                      keyField={KEY_FIELD} customStyles={customStyles} striped highlightOnHover pagination />
                  </div>
                </div>
              </Tab.Panel>

              <Tab.Panel>
                {(() => {
                  const ledger = getFilteredLedger();
                  return (
                    <div className="mb-8">
                      <div className="mt-6">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold text-blue-800">Account Statement (Ledger)</h3>
                        </div>
                        <div id="opening-closing-table">
                          <div className="mb-3 px-4 py-2 rounded bg-gray-100 dark:bg-gray-700 flex justify-between text-sm font-semibold">
                            <span>Opening Balance</span>
                            <span className="text-blue-700">₹ {formatNumber(ledger.opening_balance)}</span>
                          </div>
                          <DataTable
                            columns={ledgerColumns}
                            data={ledger.entries}
                            keyField="_row_key"
                            customStyles={customStyles}
                            pagination
                            striped
                            highlightOnHover
                            defaultSortFieldId="sno"
                            defaultSortAsc={true}
                            conditionalRowStyles={[
                              { when: (row) => row.type === "income",  style: { borderLeft: "4px solid #16a34a" } },
                              { when: (row) => row.type === "expense", style: { borderLeft: "4px solid #dc2626" } },
                            ]}
                          />
                          <div className="mt-4 border-t pt-4 flex flex-col items-end gap-2 pr-4">
                            <div className="text-sm">
                              <span className="font-semibold mr-2">Opening Balance:</span>
                              ₹ {formatNumber(ledger.opening_balance)}
                            </div>
                            <div className="text-sm text-green-700">
                              <span className="font-semibold mr-2">Total Credit (Income):</span>
                              ₹ {formatNumber(ledger.total_credit)}
                            </div>
                            <div className="text-sm">
                              <span className="font-semibold text-green-600 mr-2">Cash: ₹ {formatNumber(ledger.total_credit_cash)}</span>
                              <span className="text-gray-400 mx-2">|</span>
                              <span className="font-semibold text-green-500">Online: ₹ {formatNumber(ledger.total_credit_online)}</span>
                            </div>
                            <div className="text-sm text-red-700">
                              <span className="font-semibold mr-2">Total Debit (Expenses):</span>
                              ₹ {formatNumber(ledger.total_debit)}
                            </div>
                            <div className={`text-lg font-bold border-t-2 pt-2 ${ledger.final_balance >= 0 ? "text-blue-800 border-blue-800" : "text-red-700 border-red-700"}`}>
                              <span className="mr-2">Closing Balance:</span>
                              ₹ {formatNumber(ledger.final_balance)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
};

export default Report;